import React, { useState, useEffect, useMemo, use } from "react";
import {
  Table,
  Modal,
  Button,
  Form,
  Alert,
  Spinner,
  InputGroup,
  Card,
  Dropdown,
  Pagination,
} from "react-bootstrap";
import {
  FaTrashAlt,
  FaEdit,
  FaPlus,
  FaSearch,
  FaEllipsisV,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaBuilding,
} from "react-icons/fa";
import { APICall } from "../Utls/Api";
import { useSelector } from "react-redux";

const Subdepartments = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [subdepartments, setSubdepartments] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.user.user);

  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "success",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [subdepartmentData, setSubdepartmentData] = useState({
    name: "",
    departmentId: user?.user_type === "manager" ? user.departmentId : "",
  });
  const [currentSubdepartment, setCurrentSubdepartment] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(5);

  const showAlert = (message, variant = "success") => {
    setAlert({ show: true, message, variant });
    setTimeout(() => setAlert({ ...alert, show: false }), 3000);
  };

  useEffect(() => {
    setSubdepartmentData({
      ...subdepartmentData,
      departmentId: user?.user_type === "manager" ? user.departmentId : "",
    });
  }, [user]);
  useEffect(() => {
    fetchSubdepartments();
    fetchDepartments();
  }, []);

  const fetchSubdepartments = async () => {
    try {
      setLoading(true);
      const response = await APICall("auth/subdepartments/", "GET");
      setSubdepartments(response.data || []);
    } catch (error) {
      showAlert("Failed to fetch subdepartments", "danger");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await APICall("auth/department/all", "GET");
      setDepartments(response.departments || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  const sortedSubdepartments = useMemo(() => {
    let sortableItems = [...subdepartments];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [subdepartments, sortConfig]);

  const filteredSubdepartments = useMemo(() => {
    return sortedSubdepartments.filter((subdepartment) =>
      subdepartment.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [sortedSubdepartments, searchTerm]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredSubdepartments.slice(
    indexOfFirstRow,
    indexOfLastRow
  );
  const totalPages = Math.ceil(filteredSubdepartments.length / rowsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="ms-1" />;
    return sortConfig.direction === "asc" ? (
      <FaSortUp className="ms-1" />
    ) : (
      <FaSortDown className="ms-1" />
    );
  };

  const handleSelectRow = (id) => {
    setSelectedRows((prev) =>
      prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]
    );
  };

  const handleSelectAllRows = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentRows.map((row) => row._id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleDeleteClick = (subdepartment) => {
    setCurrentSubdepartment(subdepartment);
    setShowDeleteModal(true);
  };

  const handleEditClick = (subdepartment) => {
    setCurrentSubdepartment(subdepartment);
    setSubdepartmentData({
      name: subdepartment.name || "",
      departmentId: subdepartment.departmentId || "",
    });
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    try {
      await APICall(
        `supervisor/subdepartment/${currentSubdepartment._id}`,
        "DELETE"
      );
      showAlert("Subdepartment deleted successfully");
      fetchSubdepartments();
      setSelectedRows((prev) =>
        prev.filter((rowId) => rowId !== currentSubdepartment._id)
      );
      setShowDeleteModal(false);
    } catch (error) {
      showAlert("Failed to delete subdepartment", "danger");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSubdepartmentData({ ...subdepartmentData, [name]: value });
  };

  const handleAddSubmit = async () => {
    try {
      setLoading(true);
      await APICall("supervisor/subdepartment", "POST", subdepartmentData);
      showAlert("Subdepartment created successfully");
      setShowAddModal(false);
      setSubdepartmentData({ name: "", departmentId: "" });
      fetchSubdepartments();
    } catch (error) {
      showAlert(`Failed to create subdepartment: ${error.message}`, "danger");
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async () => {
    try {
      setLoading(true);
      const data = {
        id: currentSubdepartment._id,
        name: subdepartmentData.name,
        departmentId: subdepartmentData.departmentId,
      };
      await APICall(
        `supervisor/subdepartment/${currentSubdepartment._id}`,
        "PUT",
        data
      );
      showAlert("Subdepartment updated successfully");
      setShowEditModal(false);
      setSubdepartmentData({ name: "", departmentId: "" });
      fetchSubdepartments();
    } catch (error) {
      showAlert(`Failed to update subdepartment: ${error.message}`, "danger");
    } finally {
      setLoading(false);
    }
  };

  const getDepartmentName = (departmentId) => {
    const department = departments.find((dept) => dept._id === departmentId);
    return department ? department.name : "Unknown";
  };

  return (
    <div className="p-4">
      {alert.show && (
        <Alert
          variant={alert.variant}
          className="fade-in-alert"
          onClose={() => setAlert({ ...alert, show: false })}
          dismissible
        >
          {alert.message}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 fw-bold text-orange">Subdepartment Management</h2>
          <p className="text-muted mb-0">
            Manage subdepartments within your organization
          </p>
        </div>
        <Button
          variant="orange"
          onClick={() => setShowAddModal(true)}
          disabled={loading}
          className="d-flex align-items-center rounded-pill px-4"
        >
          <FaPlus className="me-2" />
          Add Subdepartment
        </Button>
      </div>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-3 bg-light rounded">
          <div className="d-flex justify-content-between align-items-center">
            <InputGroup style={{ width: "300px" }}>
              <InputGroup.Text className="bg-white border-end-0">
                <FaSearch className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search subdepartments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-start-0 ps-0"
              />
            </InputGroup>

            <div className="d-flex align-items-center gap-2">
              {selectedRows.length > 0 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  className="d-flex align-items-center rounded-pill"
                >
                  <FaTrashAlt className="me-1" />
                  Delete ({selectedRows.length})
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="orange" />
              <p className="mt-3">Loading subdepartments...</p>
            </div>
          ) : (
            <>
              <div className="rounded">
                <Table hover className="mb-0">
                  <thead className="bg-orange text-white">
                    <tr>
                      <th className="ps-4" style={{ width: "50px" }}>
                        <Form.Check
                          type="checkbox"
                          onChange={handleSelectAllRows}
                          checked={
                            selectedRows.length === currentRows.length &&
                            currentRows.length > 0
                          }
                        />
                      </th>
                      <th
                        className="cursor-pointer"
                        onClick={() => requestSort("name")}
                      >
                        <div className="d-flex align-items-center">
                          Subdepartment Name
                          {getSortIcon("name")}
                        </div>
                      </th>
                      <th>Department</th>
                      <th className="text-end pe-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.length > 0 ? (
                      currentRows.map((subdepartment) => (
                        <tr
                          key={subdepartment._id}
                          className={
                            selectedRows.includes(subdepartment._id)
                              ? "table-active"
                              : ""
                          }
                        >
                          <td className="ps-4">
                            <Form.Check
                              type="checkbox"
                              checked={selectedRows.includes(subdepartment._id)}
                              onChange={() =>
                                handleSelectRow(subdepartment._id)
                              }
                            />
                          </td>
                          <td>
                            <div className="fw-semibold">
                              {subdepartment.name}
                            </div>
                            <small className="text-muted">
                              ID: {subdepartment._id.substring(0, 8)}...
                            </small>
                          </td>
                          <td>
                            <span className="badge bg-secondary">
                              {getDepartmentName(subdepartment.departmentId)}
                            </span>
                          </td>
                          <td className="text-end pe-4">
                            <Dropdown>
                              <Dropdown.Toggle
                                variant="light"
                                size="sm"
                                className="px-2 rounded-pill"
                              >
                                <FaEllipsisV />
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item
                                  onClick={() => handleEditClick(subdepartment)}
                                  className="d-flex align-items-center"
                                >
                                  <FaEdit className="me-2 text-orange" />
                                  Edit
                                </Dropdown.Item>
                                <Dropdown.Item
                                  onClick={() =>
                                    handleDeleteClick(subdepartment)
                                  }
                                  className="d-flex align-items-center"
                                >
                                  <FaTrashAlt className="me-2 text-danger" />
                                  Delete
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center py-5">
                          <h5>No subdepartments found</h5>
                          <p className="text-muted">
                            Try adjusting your search or add a new subdepartment
                          </p>
                          <Button
                            variant="orange"
                            onClick={() => setShowAddModal(true)}
                            className="mt-2"
                          >
                            <FaPlus className="me-2" />
                            Add Subdepartment
                          </Button>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {filteredSubdepartments.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <div className="text-muted">
                    Showing {indexOfFirstRow + 1} to{" "}
                    {Math.min(indexOfLastRow, filteredSubdepartments.length)} of{" "}
                    {filteredSubdepartments.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                    />
                    {Array.from({ length: totalPages }, (_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => setCurrentPage(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Add Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)} centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4 fw-bold">
            Add New Subdepartment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Subdepartment Name
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter subdepartment name"
                name="name"
                value={subdepartmentData.name}
                onChange={handleChange}
                disabled={loading}
                className="py-2"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Department</Form.Label>
              <Form.Select
                name="departmentId"
                value={subdepartmentData.departmentId}
                onChange={handleChange}
                disabled={loading}
                className="py-2"
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowAddModal(false)}
            disabled={loading}
            className="rounded-pill px-4"
          >
            Cancel
          </Button>
          <Button
            variant="orange"
            onClick={handleAddSubmit}
            disabled={
              loading ||
              !subdepartmentData.name 
              // !subdepartmentData.departmentId
            }
            className="rounded-pill px-4"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Adding...
              </>
            ) : (
              "Add Subdepartment"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h4 fw-bold">Edit Subdepartment</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-0">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Subdepartment Name
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter subdepartment name"
                name="name"
                value={subdepartmentData.name}
                onChange={handleChange}
                disabled={loading}
                className="py-2"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">Department</Form.Label>
              <Form.Select
                name="departmentId"
                value={subdepartmentData.departmentId}
                onChange={handleChange}
                disabled={loading}
                className="py-2"
              >
                <option value="">Select Department</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowEditModal(false)}
            disabled={loading}
            className="rounded-pill px-4"
          >
            Cancel
          </Button>
          <Button
            variant="orange"
            onClick={handleEditSubmit}
            disabled={
              loading ||
              !subdepartmentData.name ||
              !subdepartmentData.departmentId
            }
            className="rounded-pill px-4"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Updating...
              </>
            ) : (
              "Update Subdepartment"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete subdepartment{" "}
            <strong>{currentSubdepartment?.name}</strong>?
          </p>
          <p className="text-danger">This action cannot be undone.</p>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button
            variant="light"
            onClick={() => setShowDeleteModal(false)}
            className="rounded-pill px-4"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={loading}
            className="rounded-pill px-4"
          >
            {loading ? (
              <>
                <Spinner
                  as="span"
                  size="sm"
                  animation="border"
                  role="status"
                  className="me-2"
                />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Subdepartments;
